import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApartmentLimitRequest, BillingRecord, BillingSummary,
  adminGetAllBillingRecords, adminGetBillingSummary,
  adminSuspendManager, adminActivateManager,
  adminDeleteBillingRecord, adminGenerateBills,
  adminGetLimitRequests, adminApproveLimitRequest, adminDenyLimitRequest,
} from '../api.billing';
import { getAllManagers, ManagerAdmin } from '../api.admin';

const getToken = () => localStorage.getItem('token') || '';

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  PAID:    { bg: '#f0fff4', color: '#276749' },
  PENDING: { bg: '#fffbeb', color: '#92400e' },
  OVERDUE: { bg: '#fff5f5', color: '#c53030' },
};

const BILLING_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  ACTIVE:    { bg: '#f0fff4', color: '#276749' },
  SUSPENDED: { bg: '#fff5f5', color: '#c53030' },
};

const fmtCZK = (n: number) => `${n.toLocaleString('cs-CZ')} CZK`;

const AdminBilling: React.FC = () => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [managers, setManagers] = useState<ManagerAdmin[]>([]);
  const [limitRequests, setLimitRequests] = useState<ApartmentLimitRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState<string | null>(null);
  const [approveModal, setApproveModal] = useState<ApartmentLimitRequest | null>(null);
  const [approveNewLimit, setApproveNewLimit] = useState('');
  const [approveNotes, setApproveNotes] = useState('');

  const load = async () => {
    const token = getToken();
    try {
      const [s, r, m, lr] = await Promise.all([
        adminGetBillingSummary(token),
        adminGetAllBillingRecords(token),
        getAllManagers(token),
        adminGetLimitRequests(token),
      ]);
      setSummary(s);
      setRecords(r);
      setManagers(m);
      setLimitRequests(lr);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSuspend = async (managerId: number) => {
    setActionError(null);
    try {
      await adminSuspendManager(managerId, getToken());
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleActivate = async (managerId: number) => {
    setActionError(null);
    try {
      await adminActivateManager(managerId, getToken());
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateMsg(null);
    setActionError(null);
    try {
      const res = await adminGenerateBills(getToken());
      setGenerateMsg(res.message);
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setActionError(null);
    try {
      await adminApproveLimitRequest(approveModal.id, parseInt(approveNewLimit, 10), approveNotes, getToken());
      setApproveModal(null);
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleDeny = async (id: number) => {
    setActionError(null);
    try {
      await adminDenyLimitRequest(id, '', getToken());
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleDeleteRecord = async (id: number) => {
    if (!window.confirm('Delete this billing record?')) return;
    setActionError(null);
    try {
      await adminDeleteBillingRecord(id, getToken());
      setRecords(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const filteredRecords = filterStatus === 'ALL'
    ? records
    : records.filter(r => r.status === filterStatus);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading...</div>;
  if (error) return <div style={{ padding: 16, color: '#c53030', background: '#fff5f5', borderRadius: 8 }}>{error}</div>;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>Billing</h1>
          <p style={{ color: '#718096', fontSize: '0.9rem' }}>Revenue overview and manager accounts</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{ background: '#ebf4ff', color: '#2563eb', border: '1px solid #bee3f8', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: '0.85rem', cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.7 : 1 }}
          >
            {generating ? 'Generating…' : '⚡ Generate Bills'}
          </button>
          <Link
            to="/admin/billing/settings"
            style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', color: '#4a5568', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}
          >
            ⚙ Settings
          </Link>
        </div>
      </div>

      {actionError && (
        <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, padding: '10px 14px', color: '#c53030', marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      {generateMsg && (
        <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, padding: '10px 14px', color: '#276749', marginBottom: 16 }}>
          {generateMsg}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
          <SummaryCard label="Revenue Paid" value={fmtCZK(summary.totalPaid)} color="#276749" />
          <SummaryCard label="Pending" value={fmtCZK(summary.totalDue)} color="#92400e" />
          <SummaryCard label="Overdue" value={fmtCZK(summary.totalOverdue)} color="#c53030" />
          <SummaryCard label="Total Records" value={summary.recordCount} color="#4a5568" />
        </div>
      )}

      {/* Managers table */}
      <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', marginBottom: 14 }}>Users</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={th}>User</th>
                <th style={th}>Email</th>
                <th style={th}>Billing</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {managers.map(m => {
                const status = m.billingStatus || 'ACTIVE';
                const sc = BILLING_STATUS_COLORS[status] || BILLING_STATUS_COLORS.ACTIVE;
                const name = [m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || m.user.email;
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                    <td style={td}>
                      <Link to={`/admin/billing/${m.id}`} style={{ color: '#2563eb', fontWeight: 600 }}>{name}</Link>
                    </td>
                    <td style={td}>{m.user.email}</td>
                    <td style={td}>
                      <span style={{ ...badgeStyle, background: sc.bg, color: sc.color }}>{status}</span>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {status === 'ACTIVE' ? (
                          <button onClick={() => handleSuspend(m.id)} style={dangerBtn}>Suspend</button>
                        ) : (
                          <button onClick={() => handleActivate(m.id)} style={successBtn}>Activate</button>
                        )}
                        <Link to={`/admin/billing/${m.id}`} style={detailBtn}>Detail →</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {managers.length === 0 && <div style={{ color: '#a0aec0', padding: '16px 0', textAlign: 'center' }}>No users</div>}
        </div>
      </section>

      {/* Apartment limit increase requests */}
      {limitRequests.filter(r => r.status === 'PENDING').length > 0 && (
        <section style={{ background: '#fffbeb', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #f6ad55', marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#92400e', marginBottom: 14 }}>
            Apartment Limit Requests ({limitRequests.filter(r => r.status === 'PENDING').length} pending)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f6ad55' }}>
                  <th style={th}>Manager</th>
                  <th style={th}>Current / Limit</th>
                  <th style={th}>Message</th>
                  <th style={th}>Requested</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {limitRequests.filter(r => r.status === 'PENDING').map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #fef3c7' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.managerName}</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>{r.managerEmail}</div>
                    </td>
                    <td style={td}>{r.currentCount} / {r.currentLimit}</td>
                    <td style={{ ...td, maxWidth: 220, color: r.message ? '#2d3748' : '#a0aec0' }}>
                      {r.message || <em>No message</em>}
                    </td>
                    <td style={td}>{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => { setApproveModal(r); setApproveNewLimit(String(r.currentLimit + 10)); setApproveNotes(''); }}
                          style={successBtn}
                        >
                          Approve
                        </button>
                        <button onClick={() => handleDeny(r.id)} style={dangerBtn}>Deny</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Billing records */}
      <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>All Billing Records</div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: '0.85rem' }}
          >
            <option value="ALL">All statuses</option>
            <option value="PAID">Paid</option>
            <option value="PENDING">Pending</option>
            <option value="OVERDUE">Overdue</option>
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={th}>Manager</th>
                <th style={th}>Period</th>
                <th style={th}>Apts</th>
                <th style={th}>Price/Apt</th>
                <th style={th}>Total</th>
                <th style={th}>Cycle</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.PENDING;
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                    <td style={td}>{r.managerName}</td>
                    <td style={td}>{r.periodYear}/{String(r.periodMonth).padStart(2, '0')}</td>
                    <td style={td}>{r.apartmentCount}</td>
                    <td style={td}>{fmtCZK(r.pricePerApartment)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtCZK(r.totalAmount)}</td>
                    <td style={td}>{r.billingCycle}</td>
                    <td style={td}>
                      <span style={{ ...badgeStyle, background: sc.bg, color: sc.color }}>{r.status}</span>
                    </td>
                    <td style={td}>
                      <button onClick={() => handleDeleteRecord(r.id)} style={dangerBtn}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredRecords.length === 0 && <div style={{ color: '#a0aec0', padding: '16px 0', textAlign: 'center' }}>No records</div>}
        </div>
      </section>
      {/* Approve limit request modal */}
      {approveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>Approve limit increase</h2>
            <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: 16 }}>
              {approveModal.managerName} currently has {approveModal.currentCount} apartments (limit: {approveModal.currentLimit}).
            </p>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>New limit</label>
            <input
              type="number"
              min={approveModal.currentCount}
              value={approveNewLimit}
              onChange={e => setApproveNewLimit(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e0', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: 12 }}
            />
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>
              Note to manager <span style={{ color: '#a0aec0', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              value={approveNotes}
              onChange={e => setApproveNotes(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e0', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleApprove} style={{ flex: 1, padding: '10px', background: '#276749', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Approve
              </button>
              <button onClick={() => setApproveModal(null)} style={{ padding: '10px 16px', background: '#f7fafc', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
    <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.35rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#4a5568', fontSize: '0.8rem' };
const td: React.CSSProperties = { padding: '10px 10px', color: '#2d3748', verticalAlign: 'middle' };
const badgeStyle: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 };
const dangerBtn: React.CSSProperties = { background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 };
const successBtn: React.CSSProperties = { background: '#f0fff4', color: '#276749', border: '1px solid #9ae6b4', borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 };
const detailBtn: React.CSSProperties = { background: '#ebf4ff', color: '#2563eb', border: '1px solid #bee3f8', borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600, textDecoration: 'none', display: 'inline-block' };

export default AdminBilling;
